import type { IPeerConnection } from '../types';

type Replacement = {
  oldTrack: MediaStreamTrack;
  newTrack: MediaStreamTrack;
  completion: Promise<void>;
};

/** Coalesces the capture event and its awaiting caller into one sender update. */
export class PeerTrackReplacementManager {
  private readonly replacements = new WeakMap<IPeerConnection, Map<string, Replacement>>();

  public replace(
    connection: IPeerConnection,
    oldTrack: MediaStreamTrack,
    newTrack: MediaStreamTrack,
    onReplaced: () => void,
  ): Promise<void> {
    if (oldTrack === newTrack) return Promise.resolve();
    let byKind = this.replacements.get(connection);
    if (!byKind) {
      byKind = new Map();
      this.replacements.set(connection, byKind);
    }
    const previous = byKind.get(newTrack.kind);
    if (previous?.oldTrack === oldTrack && previous.newTrack === newTrack) return previous.completion;

    const replacement: Replacement = {
      oldTrack,
      newTrack,
      completion: Promise.resolve().then(async () => {
        try {
          await connection.replaceTrack(oldTrack, newTrack);
          onReplaced();
        } catch (error) {
          // Failed swaps may be retried; do not memoize a rejection.
          if (byKind.get(newTrack.kind) === replacement) byKind.delete(newTrack.kind);
          throw error;
        }
      }),
    };
    byKind.set(newTrack.kind, replacement);
    return replacement.completion;
  }
}
